import React from 'react';
import { Stack, SxProps, Theme } from '@mui/material';

import { PropertySelectField } from '@paragon/client-tools';
import { naturalSortByProperty } from '@paragon/utilities';

import { ensureLevelIsVisible } from '../../../../store/actions/levelVisibilityActions';
import { getAllFeaturePatternContainers } from '../../../../store/selectors/layoutDataSelectors';
import { getLayoutHierarchyAccessLevel } from '../../../../store/selectors/layoutModeSelectors';
import { ghostObjectVisibilityEnabled } from '../../../../store/slices/selectionsSlice';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { useBuildingLevelHierarchy } from './useBuildingLevelHierarchy';

interface ProjectHierarchyContextProps {
  readonly levelGuid: string | null;
  readonly onChange: (levelGuid: string | null) => void;

  /** Only used when moving feature pattern envelopes, which must belong to a container in the same level */
  readonly featurePatternContainer?: {
    readonly guid: string | null;
    readonly onChange: (featurePatternContainerGuid: string | null) => void;
  };

  readonly sx?: SxProps<Theme>;
  readonly disabled?: boolean;
}

interface ProjectHierarchyOption {
  readonly guid: string;
  readonly name: string;
}

const noSelectionIdentifier: ProjectHierarchyOption = { name: '<None>', guid: '<None>' };

export function ProjectHierarchyContextSelects({
  sx,
  disabled,
  levelGuid,
  onChange,
  featurePatternContainer,
}: ProjectHierarchyContextProps) {
  const dispatch = useAppDispatch();

  const { buildings, levels, buildingsWithLevels, levelsIn, buildingContaining } = useBuildingLevelHierarchy();
  const featurePatternContainers = useAppSelector(getAllFeaturePatternContainers);

  const hierarchyAccess = useAppSelector(getLayoutHierarchyAccessLevel);

  const selectedBuildingGuid = buildingContaining(levelGuid);

  const buildingOptions = [noSelectionIdentifier].concat(buildingsWithLevels);

  const levelsForSelectedBuilding = levelsIn(selectedBuildingGuid);
  const levelOptions = levelsForSelectedBuilding.length === 0 ? [noSelectionIdentifier] : levelsForSelectedBuilding;

  const featurePatternContainersInLevel = featurePatternContainers.filter(
    (container) => container.levelGuid === levelGuid,
  );
  const featurePatternContainerOptions =
    featurePatternContainersInLevel.length === 0
      ? [noSelectionIdentifier]
      : naturalSortByProperty(featurePatternContainersInLevel, ({ name }) => name);

  function handleBuildingChange(buildingOption: ProjectHierarchyOption) {
    const levelsForNewlySelectedBuilding = naturalSortByProperty(levelsIn(buildingOption.guid), (level) => level.name);
    const levelGuid =
      buildingOption.guid === noSelectionIdentifier.guid || levelsForNewlySelectedBuilding.length === 0
        ? null
        : levelsForNewlySelectedBuilding[0].guid;

    if (levelGuid != null) {
      dispatch(ensureLevelIsVisible(levelGuid, { makeActive: true }));
    } else {
      dispatch(ghostObjectVisibilityEnabled());
    }

    onChange(levelGuid);
  }

  function handleLevelChange(levelOption: ProjectHierarchyOption) {
    const levelGuid = levelOption.guid === noSelectionIdentifier.guid ? null : levelOption.guid;
    if (levelGuid == null) return;

    onChange(levelGuid);

    // ensure level is visible and mark it as the active level
    dispatch(ensureLevelIsVisible(levelGuid, { makeActive: true }));
  }

  function handleFeaturePatternContainerChange(featurePatternContainerOption: ProjectHierarchyOption) {
    featurePatternContainer?.onChange(
      featurePatternContainerOption.guid === noSelectionIdentifier.guid ? null : featurePatternContainerOption.guid,
    );
  }

  if (!featurePatternContainer && !hierarchyAccess.read) return null;

  return (
    <Stack direction="row" spacing={1} sx={sx}>
      {hierarchyAccess.read && (
        <>
          <PropertySelectField<ProjectHierarchyOption>
            property="Building"
            value={selectedBuildingGuid ? buildings[selectedBuildingGuid] : noSelectionIdentifier}
            options={buildingOptions}
            getName={(option) => option.name}
            getValue={(option) => option.guid}
            submit={handleBuildingChange}
            margin="none"
            disabled={disabled || !hierarchyAccess.write}
          />

          <PropertySelectField<ProjectHierarchyOption>
            property="Level"
            value={levelGuid ? levels[levelGuid] : noSelectionIdentifier}
            options={levelOptions}
            getName={(option) => option.name}
            getValue={(option) => option.guid}
            disabled={disabled || !hierarchyAccess.write || selectedBuildingGuid === null}
            submit={handleLevelChange}
            margin="none"
          />
        </>
      )}

      {featurePatternContainer && (
        <PropertySelectField<ProjectHierarchyOption>
          property="Feature Pattern Container"
          value={
            featurePatternContainer.guid
              ? featurePatternContainers.find(({ guid }) => guid === featurePatternContainer.guid)!
              : noSelectionIdentifier
          }
          options={featurePatternContainerOptions}
          getName={(option) => option.name}
          getValue={(option) => option.guid}
          disabled={disabled || selectedBuildingGuid === null || levelGuid === null}
          submit={handleFeaturePatternContainerChange}
          margin="none"
        />
      )}
    </Stack>
  );
}
